import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { popupsApi } from '@/services/popupsApi';
import { PopupDialog } from './PopupDialog';
import { dismissPopup, koreaDay, popupHidden } from './popupDismissal';

const excluded = ['/admin', '/login', '/register', '/social-register', '/verify-phone', '/desktop-auth-complete', '/checkout', '/payment', '/share', '/find-id', '/forgot-password', '/reset-password'];
const DAY = 24 * 60 * 60 * 1000;
const KOREA_OFFSET = 9 * 60 * 60 * 1000;

// The desktop notice opens its link (the manual) in the browser; the same notice must not cover it there.
function linksHere(link: string, pathname: string): boolean {
  try {
    const url = new URL(link, window.location.origin);
    return url.origin === window.location.origin && url.pathname.replace(/\/+$/, '') === pathname.replace(/\/+$/, '');
  } catch {
    return false;
  }
}

export function SitePopup() {
  const { pathname } = useLocation();
  const enabled = !excluded.some(path => pathname === path || pathname.startsWith(path + '/'));
  // null until checked for the current display: never flash the notice over another dialog.
  const [otherDialog, setOtherDialog] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState<Record<string, string>>({});
  const [now, setNow] = useState(Date.now());
  const { data: popup } = useQuery({ queryKey: ['site-popup'], queryFn: popupsApi.active, enabled, staleTime: 60_000, refetchInterval: 60_000, retry: false });
  const closed = popup ? dismissed[popup.id] : undefined;
  const visible = Boolean(enabled && popup && !popupHidden(popup.id, new Date(now))
    && closed !== 'session' && closed !== koreaDay(new Date(now))
    && !(popup.expires_at && Date.parse(popup.expires_at) <= now) && !linksHere(popup.link_url, pathname));
  useEffect(() => {
    if (!visible) return;
    const update = () => setOtherDialog(Boolean(document.querySelector('[role="dialog"]:not([data-site-popup]),[role="alertdialog"]')));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] });
    return () => { observer.disconnect(); setOtherDialog(null); };
  }, [visible]);
  useEffect(() => {
    const update = () => setNow(Date.now());
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);
  useEffect(() => {
    // Re-render only when the answer can change: at the popup's expiry, or at Korea
    // midnight when a "today" dismissal ends. Both are at most a day away.
    if (!enabled || !popup) return;
    const midnight = (Math.floor((now + KOREA_OFFSET) / DAY) + 1) * DAY - KOREA_OFFSET;
    const expiry = popup.expires_at ? Date.parse(popup.expires_at) : NaN;
    const next = expiry > now ? Math.min(expiry, midnight) : midnight;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, next - Date.now()) + 20);
    return () => window.clearTimeout(timer);
  }, [enabled, popup, now]);
  if (!visible || !popup || otherDialog === null) return null;
  return <PopupDialog key={popup.id} popup={popup} hidden={otherDialog} onClose={remember => {
    dismissPopup(popup, remember);
    setDismissed(old => ({ ...old, [popup.id]: remember && popup.dismiss_mode === 'today' ? koreaDay() : 'session' }));
  }} />;
}
